import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiStatsBar } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface PayPeriodEmployee {
  user_id: number | null
  name: string
  pay_rate: number | null
  pay_type: number | null
  worker_type: number | null
  otj_minutes: number
  travel_minutes: number
  regular_minutes: number
  overtime_minutes: number
  time_pay: number
  flat_rate_total: number
  mileage: number
  mileage_total: number
  expenses: number
  pto_hours: number
  sto_hours: number
  unavail_days: number
  week1_otj_minutes: number
  week1_travel_minutes: number
  week1_regular_minutes: number
  week1_overtime_minutes: number
  week1_pto_hours: number
  week1_sto_hours: number
  week1_unavail_days: number
  week2_otj_minutes: number
  week2_travel_minutes: number
  week2_regular_minutes: number
  week2_overtime_minutes: number
  week2_pto_hours: number
  week2_sto_hours: number
  week2_unavail_days: number
  has_unapproved: boolean
}

interface PayPeriod {
  calc_type: number
  method_description: string
  week1_start: string
  week1_end: string
  week1_number: number
  week2_start: string
  week2_end: string
  week2_number: number
  employees: PayPeriodEmployee[]
  totals: PayPeriodEmployee | null
  unpaid_time_entry_count: number
}

interface LogNote {
  id: number
  date: string | null
  employee: string | null
  content: string
  hidden: boolean
}

interface Props {
  pay_period: PayPeriod
  log_notes: LogNote[]
}

/* ── Helpers ────────────────────────────────────────────── */

function TimeDisplay({ minutes }: { minutes: number }) {
  if (minutes === 0) return null
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return (
    <span className="tabular-nums">
      {h}h{" "}
      <span className={m === 0 ? "text-hpi-med-gray" : ""}>
        {String(m).padStart(2, "0")}m
      </span>
    </span>
  )
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00")
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T12:00:00")
  d.setDate(d.getDate() + days)
  return d.toISOString().split("T")[0]
}

/* ── Table Components ─────────────────────────────────── */

function TableHeader({ label }: { label: string }) {
  return (
    <tr>
      <td
        colSpan={9}
        className="bg-hpi-dark-blue rounded-t px-4 py-2.5 text-sm font-semibold tracking-wider text-white uppercase"
      >
        {label}
      </td>
    </tr>
  )
}

function PeriodColumnHeaders() {
  return (
    <tr className="border-hpi-light-gray text-hpi-active-blue border-b bg-white text-center text-[11px] font-medium">
      <th className="border-hpi-light-gray border-r-2 px-2 py-2 text-left font-medium">
        Employee
      </th>
      <th className="px-2 py-2 font-medium">OTJ</th>
      <th className="px-2 py-2 font-medium">Travel</th>
      <th className="border-hpi-light-gray border-r-2 px-2 py-2 font-medium">
        Total Hours
      </th>
      <th className="px-2 py-2 font-medium">Reg</th>
      <th className="border-hpi-light-gray border-r-2 px-2 py-2 font-medium">
        OT
      </th>
      <th className="px-1 py-2 leading-tight font-medium">
        PTO
        <br />
        days
      </th>
      <th className="px-1 py-2 leading-tight font-medium">
        STO
        <br />
        hours
      </th>
      <th className="px-1 py-2 leading-tight font-medium">
        Unavail
        <br />
        days
      </th>
    </tr>
  )
}

function WeekColumnHeaders() {
  return (
    <tr className="border-hpi-light-gray text-hpi-active-blue border-b bg-white text-center text-[11px] font-medium">
      <th className="border-hpi-light-gray border-r-2 px-2 py-2 text-left font-medium">
        Employee
      </th>
      <th className="px-2 py-2 font-medium">OTJ</th>
      <th className="px-2 py-2 font-medium">Travel</th>
      <th className="border-hpi-light-gray border-r-2 px-2 py-2 font-medium">
        Total Hours
      </th>
      <th className="px-2 py-2 font-medium" />
      <th className="px-2 py-2 font-medium" />
      <th className="border-hpi-light-gray border-l-2 px-1 py-2 leading-tight font-medium">
        PTO
        <br />
        days
      </th>
      <th className="px-1 py-2 leading-tight font-medium">
        STO
        <br />
        hours
      </th>
      <th className="px-1 py-2 leading-tight font-medium">
        Unavail
        <br />
        days
      </th>
    </tr>
  )
}

function PeriodRow({
  emp,
  reportStart,
  reportEnd,
}: {
  emp: PayPeriodEmployee
  reportStart: string
  reportEnd: string
}) {
  const totalMinutes = emp.otj_minutes + emp.travel_minutes
  const ptoDays = emp.pto_hours > 0 ? emp.pto_hours / 8 : 0

  return (
    <tr className="border-hpi-light-gray/50 text-hpi-dark-gray border-b bg-white text-sm">
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-left font-semibold">
        {emp.user_id ? (
          <Link
            href={`/accounting/payroll/detail?employee_id=${emp.user_id}&start_date=${reportStart}&end_date=${reportEnd}`}
            className="text-hpi-dark-gray hover:text-hpi-active-blue"
          >
            {emp.name}
            {emp.pay_type != null && (
              <span className="ml-1.5 text-[10px] font-bold">
                <span
                  className={
                    emp.pay_type === 1
                      ? "text-amber-600"
                      : "text-hpi-active-blue"
                    /* 0=Salary, 1=Hourly */
                  }
                >
                  {emp.pay_type === 0 ? "SAL" : "HR"}
                </span>
                /
                <span
                  className={
                    emp.worker_type === 1
                      ? "text-amber-600"
                      : "text-hpi-active-blue"
                  }
                >
                  {emp.worker_type === 1 ? "1099" : "W2"}
                </span>
              </span>
            )}
          </Link>
        ) : (
          <span>{emp.name}</span>
        )}
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={emp.otj_minutes} />
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={emp.travel_minutes} />
      </td>
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-right">
        <TimeDisplay minutes={totalMinutes} />
      </td>
      <td className="px-2 py-2 text-right font-bold">
        <TimeDisplay minutes={emp.regular_minutes} />
      </td>
      <td className="text-hpi-red border-hpi-light-gray border-r-2 px-2 py-2 text-right font-bold">
        <TimeDisplay minutes={emp.overtime_minutes} />
      </td>
      <td className="px-1 py-2 text-right">
        {ptoDays > 0 ? Math.round(ptoDays * 10) / 10 : ""}
      </td>
      <td className="px-1 py-2 text-right">
        {emp.sto_hours > 0 ? Math.round(emp.sto_hours * 10) / 10 : ""}
      </td>
      <td className="px-1 py-2 text-right">
        {emp.unavail_days > 0 ? Math.round(emp.unavail_days) : ""}
      </td>
    </tr>
  )
}

function TotalRow({ data }: { data: PayPeriodEmployee }) {
  const totalMinutes = data.otj_minutes + data.travel_minutes
  const ptoDays = data.pto_hours > 0 ? data.pto_hours / 8 : 0

  return (
    <tr className="border-hpi-light-gray text-hpi-dark-gray border-t-2 bg-white text-sm font-bold">
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-left">
        Total
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={data.otj_minutes} />
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={data.travel_minutes} />
      </td>
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-right">
        <TimeDisplay minutes={totalMinutes} />
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={data.regular_minutes} />
      </td>
      <td className="text-hpi-red border-hpi-light-gray border-r-2 px-2 py-2 text-right">
        <TimeDisplay minutes={data.overtime_minutes} />
      </td>
      <td className="px-1 py-2 text-right">
        {ptoDays > 0 ? Math.round(ptoDays * 10) / 10 : ""}
      </td>
      <td className="px-1 py-2 text-right">
        {data.sto_hours > 0 ? Math.round(data.sto_hours * 10) / 10 : ""}
      </td>
      <td className="px-1 py-2 text-right">
        {data.unavail_days > 0 ? Math.round(data.unavail_days) : ""}
      </td>
    </tr>
  )
}

function WeekRow({
  emp,
  weekNum,
  weekStart,
}: {
  emp: PayPeriodEmployee
  weekNum: 1 | 2
  weekStart: string
}) {
  const otj = weekNum === 1 ? emp.week1_otj_minutes : emp.week2_otj_minutes
  const travel =
    weekNum === 1 ? emp.week1_travel_minutes : emp.week2_travel_minutes
  const pto = weekNum === 1 ? emp.week1_pto_hours : emp.week2_pto_hours
  const sto = weekNum === 1 ? emp.week1_sto_hours : emp.week2_sto_hours
  const unavail =
    weekNum === 1 ? emp.week1_unavail_days : emp.week2_unavail_days
  const totalMinutes = otj + travel
  const ptoDays = pto > 0 ? pto / 8 : 0

  return (
    <tr className="border-hpi-light-gray/50 text-hpi-dark-gray border-b bg-white text-sm">
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-left font-semibold">
        {emp.user_id ? (
          <Link
            href={`/accounting/payroll/detail?employee_id=${emp.user_id}&start_date=${weekStart}`}
            className="text-hpi-dark-gray hover:text-hpi-active-blue"
          >
            {emp.name}
          </Link>
        ) : (
          <span>{emp.name}</span>
        )}
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={otj} />
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={travel} />
      </td>
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-right">
        <TimeDisplay minutes={totalMinutes} />
      </td>
      <td className="px-2 py-2" />
      <td className="px-2 py-2" />
      <td className="border-hpi-light-gray border-l-2 px-1 py-2 text-right">
        {ptoDays > 0 ? Math.round(ptoDays * 10) / 10 : ""}
      </td>
      <td className="px-1 py-2 text-right">
        {sto > 0 ? Math.round(sto * 10) / 10 : ""}
      </td>
      <td className="px-1 py-2 text-right">
        {unavail > 0 ? Math.round(unavail) : ""}
      </td>
    </tr>
  )
}

function WeekTotalRow({
  employees,
  weekNum,
}: {
  employees: PayPeriodEmployee[]
  weekNum: 1 | 2
}) {
  const otj = employees.reduce(
    (s, e) => s + (weekNum === 1 ? e.week1_otj_minutes : e.week2_otj_minutes),
    0,
  )
  const travel = employees.reduce(
    (s, e) =>
      s + (weekNum === 1 ? e.week1_travel_minutes : e.week2_travel_minutes),
    0,
  )
  const pto = employees.reduce(
    (s, e) => s + (weekNum === 1 ? e.week1_pto_hours : e.week2_pto_hours),
    0,
  )
  const sto = employees.reduce(
    (s, e) => s + (weekNum === 1 ? e.week1_sto_hours : e.week2_sto_hours),
    0,
  )
  const unavail = employees.reduce(
    (s, e) => s + (weekNum === 1 ? e.week1_unavail_days : e.week2_unavail_days),
    0,
  )
  const totalMinutes = otj + travel
  const ptoDays = pto > 0 ? pto / 8 : 0

  return (
    <tr className="border-hpi-light-gray text-hpi-dark-gray border-t-2 bg-white text-sm font-bold">
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-left">
        Total
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={otj} />
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={travel} />
      </td>
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-right">
        <TimeDisplay minutes={totalMinutes} />
      </td>
      <td className="px-2 py-2" />
      <td className="px-2 py-2" />
      <td className="border-hpi-light-gray border-l-2 px-1 py-2 text-right">
        {ptoDays > 0 ? Math.round(ptoDays * 10) / 10 : ""}
      </td>
      <td className="px-1 py-2 text-right">
        {sto > 0 ? Math.round(sto * 10) / 10 : ""}
      </td>
      <td className="px-1 py-2 text-right">
        {unavail > 0 ? Math.round(unavail) : ""}
      </td>
    </tr>
  )
}

const COL_WIDTHS = (
  <colgroup>
    <col style={{ width: "22%" }} />
    <col style={{ width: "12%" }} />
    <col style={{ width: "11%" }} />
    <col style={{ width: "12%" }} />
    <col style={{ width: "11%" }} />
    <col style={{ width: "11%" }} />
    <col style={{ width: "7%" }} />
    <col style={{ width: "7%" }} />
    <col style={{ width: "7%" }} />
  </colgroup>
)

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Accounting", href: "/accounting/expenses" },
  { title: "Payroll", href: "/accounting/payroll" },
  { title: "Sample", href: "/accounting/payroll/sample" },
]

export default function PayrollSample({ pay_period, log_notes }: Props) {
  const [endDate, setEndDate] = useState(pay_period.week2_end)

  const handleChangeDateRange = () => {
    router.get("/accounting/payroll/sample", { end_date: endDate })
  }

  const prevEndDate = addDays(pay_period.week2_end, -14)
  const nextEndDate = addDays(pay_period.week2_end, 14)

  const week1Employees = pay_period.employees.filter(
    (e) =>
      e.week1_otj_minutes > 0 ||
      e.week1_pto_hours > 0 ||
      e.week1_sto_hours > 0 ||
      e.week1_unavail_days > 0,
  )
  const week2Employees = pay_period.employees.filter(
    (e) =>
      e.week2_otj_minutes > 0 ||
      e.week2_pto_hours > 0 ||
      e.week2_sto_hours > 0 ||
      e.week2_unavail_days > 0,
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Payroll Sample" />

      <div className="p-6">
        {/* Title + Disclaimer */}
        <div className="mb-6 text-center">
          <h2 className="text-hpi-dark-gray text-xl font-semibold">
            Payroll Report
          </h2>
          <div className="text-hpi-med-gray text-sm">
            {formatDate(pay_period.week1_start)} to{" "}
            {formatDate(pay_period.week2_end)}
          </div>
          {log_notes.length > 0 && (
            <div className="mt-1">
              <a
                href="#logs"
                className="text-hpi-active-blue text-xs hover:underline"
              >
                Jump to Log Notes ({log_notes.length})
              </a>
            </div>
          )}
          <div className="mx-auto mt-3 max-w-2xl rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700">
            This shows payroll calculated in 2 week blocks, 80 hours to
            overtime, travel time is not included in overtime calculation.
            <br />
            This is a sample, it doesn&apos;t affect anything else.
          </div>
        </div>

        <HpiStatsBar
          stats={[
            {
              label: "Pay Period",
              subLabel: `Wk ${pay_period.week1_number}–${pay_period.week2_number}`,
              value: `${formatDate(pay_period.week1_start)} to ${formatDate(pay_period.week2_end)}`,
            },
            {
              label: "Employees",
              subLabel: "Active",
              value: pay_period.employees.length,
            },
            ...(log_notes.length > 0
              ? [
                  {
                    label: "Log Notes",
                    subLabel: "This Period",
                    value: log_notes.length,
                  },
                ]
              : []),
          ]}
          className="mb-6"
        />

        {/* Date Range Picker */}
        <div className="border-hpi-light-gray mb-6 flex items-end gap-3 rounded-lg border bg-white px-4 py-3">
          <div>
            <label className="text-hpi-active-blue mb-1 block text-xs font-medium">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-hpi-light-gray focus:border-hpi-active-blue rounded border px-3 py-1.5 text-sm focus:outline-none"
            />
          </div>
          <button
            onClick={handleChangeDateRange}
            className="bg-hpi-dark-green rounded px-4 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            Change Date Range
          </button>
        </div>

        {/* Tables */}
        {pay_period.employees.length > 0 && (
          <div className="space-y-6">
            {/* Total for Pay Period */}
            <div className="border-hpi-light-gray overflow-hidden rounded-lg border">
              <table className="w-full border-collapse">
                {COL_WIDTHS}
                <thead>
                  <TableHeader
                    label={`Total for Pay Period ${formatDate(pay_period.week1_start)} to ${formatDate(pay_period.week2_end)}`}
                  />
                  <PeriodColumnHeaders />
                </thead>
                <tbody>
                  {pay_period.employees.map((emp) => (
                    <PeriodRow
                      key={emp.user_id ?? "total"}
                      emp={emp}
                      reportStart={pay_period.week1_start}
                      reportEnd={pay_period.week2_end}
                    />
                  ))}
                  {pay_period.totals && <TotalRow data={pay_period.totals} />}
                </tbody>
              </table>
            </div>

            {/* Week 1 */}
            <div className="border-hpi-light-gray overflow-hidden rounded-lg border">
              <table className="w-full border-collapse">
                {COL_WIDTHS}
                <thead>
                  <TableHeader
                    label={`Week ${pay_period.week1_number}: ${formatDate(pay_period.week1_start)} to ${formatDate(pay_period.week1_end)}`}
                  />
                  <WeekColumnHeaders />
                </thead>
                <tbody>
                  {week1Employees.map((emp) => (
                    <WeekRow
                      key={emp.user_id ?? "w1-total"}
                      emp={emp}
                      weekNum={1}
                      weekStart={pay_period.week1_start}
                    />
                  ))}
                  <WeekTotalRow employees={pay_period.employees} weekNum={1} />
                </tbody>
              </table>
            </div>

            {/* Week 2 */}
            <div className="border-hpi-light-gray overflow-hidden rounded-lg border">
              <table className="w-full border-collapse">
                {COL_WIDTHS}
                <thead>
                  <TableHeader
                    label={`Week ${pay_period.week2_number}: ${formatDate(pay_period.week2_start)} to ${formatDate(pay_period.week2_end)}`}
                  />
                  <WeekColumnHeaders />
                </thead>
                <tbody>
                  {week2Employees.map((emp) => (
                    <WeekRow
                      key={emp.user_id ?? "w2-total"}
                      emp={emp}
                      weekNum={2}
                      weekStart={pay_period.week2_start}
                    />
                  ))}
                  <WeekTotalRow employees={pay_period.employees} weekNum={2} />
                </tbody>
              </table>
            </div>
          </div>
        )}

        {pay_period.employees.length === 0 && (
          <div className="border-hpi-light-gray text-hpi-med-blue rounded-lg border bg-white p-8 text-center">
            No payroll data found for this period.
          </div>
        )}

        {/* Previous / Next Navigation */}
        <div className="text-hpi-dark-gray mt-6 text-center text-sm">
          <span>&laquo; </span>
          <Link
            href={`/accounting/payroll/sample?end_date=${prevEndDate}`}
            className="text-hpi-active-blue hover:underline"
          >
            Previous Billing Period
          </Link>
          <span className="mx-1">|</span>
          <Link
            href={`/accounting/payroll/sample?end_date=${nextEndDate}`}
            className="text-hpi-active-blue hover:underline"
          >
            Next Billing Period
          </Link>
          <span> &raquo;</span>
        </div>

        {/* Log Notes */}
        {log_notes.length > 0 && (
          <div id="logs" className="mt-8">
            <div className="border-hpi-light-gray mx-auto max-w-3xl rounded-lg border bg-white p-5">
              <h3 className="text-hpi-dark-gray mb-4 text-base font-semibold">
                Log Entries
                <Link
                  href="/reports/employee_logs"
                  className="text-hpi-active-blue ml-2 text-xs font-normal hover:underline"
                >
                  View All
                </Link>
              </h3>
              <div className="space-y-3">
                {log_notes.map((note) => (
                  <div
                    key={note.id}
                    className={note.hidden ? "text-hpi-med-gray" : ""}
                  >
                    <div className="text-sm font-bold">
                      {note.date ? formatDate(note.date) : ""} -{" "}
                      {note.employee ?? ""}
                    </div>
                    <div className="text-xs leading-relaxed whitespace-pre-wrap">
                      {note.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
